import {memo, useCallback, useEffect, useMemo, useState} from 'react';
import PageBlock from "../../../components/PageBlock";
import Card from "../../../components/Card";
import {Button, CardBody} from "@material-tailwind/react";
import DataTable from "../../../components/DataTable/DataTable";
import TD from "../../../components/DataTable/TD";
import {API} from "../../../types/API";
import {DataTableRequest} from "../../../types/Requests/API/DataTableRequest";
import {AxiosResponse} from "axios";
import {useTranslation} from "react-i18next";
import {useDispatch} from "react-redux";
import {useUserContext} from "../../../types/contexts/UserContext";
import {setSidebarTitle} from "../../../reducers/sidebar";
import usePersistedDataTableRequest from "../../../hooks/usePersistedDataTableRequest";
import {useToastContext} from "../../../types/contexts/ToastContext";
import {useErrorHandler} from "react-error-boundary";
import {ErrorHandler} from "../../../hooks/errorHandler";
import Forbidden from "../../../components/Forbidden";
import {setTitle} from "../../../hooks/setTitle";
import {APILink} from "../../../types/APILink";
import {
    ReportingSubscriptionJobsResponse,
    ReportingSubscriptionRecord,
    ReportingSubscriptionsResponse,
    ReportingType,
    ReportingSubscriptionJob
} from "../../../types/Responses/ReportingSubscriptionsResponse";
import FilterChips, {FilterChipDefinition} from "../../../components/FilterChips";
import useComponentVisible from "../../../hooks/useComponentVisible";
import Modal from "../../../components/Modal/Modal";
import DropdownInput from "../../../components/DropdownInput";
import {UserListResponse} from "../../../types/Responses/UsersResponse";
import {UserShortInfo} from "../../../types/Responses/PayoutLimitsResponse";
import {Switch} from "@mui/material";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {solid} from "@fortawesome/fontawesome-svg-core/import.macro";
import {checkEnabled2FA} from "../../../hooks/checkEnabled2FA";
import {useSystemContext} from "../../../types/contexts/SystemContext";
import ReportingSubscriptionModal from "../../../components/reporting/ReportingSubscriptionModal";
import FilterTagGroups, {FilterTagGroupDisplay} from "../../../components/FilterTagGroups";
import {buildReportingFilterGroups, FilterGroupKey, ReportingFilterLookups, SimpleEntity} from "../../../utils/reportingFilters";
import {CountriesShortResponse} from "../../../types/Responses/CountriesResponse";
import moment from "moment";

interface ReportingSubscriptionRequest extends DataTableRequest {
    reporting_type_id?: number;
    status_id?: number;
}

const Subscriptions = () => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const user = useUserContext();
    const toast = useToastContext();
    const system = useSystemContext();
    const handleError = useErrorHandler();

    const defaultRequest = useMemo<ReportingSubscriptionRequest>(() => ({
        length: 10,
        page: 1,
        sort_by: 'id',
        sort_direction: 0
    }), []);
    const [dataTableRequest, persistDataTableRequest] = usePersistedDataTableRequest<ReportingSubscriptionRequest>(defaultRequest);

    const [subscriptions, setSubscriptions] = useState<ReportingSubscriptionsResponse>();
    const [types, setTypes] = useState<ReportingType[]>([]);
    const [usersList, setUsersList] = useState<UserShortInfo[]>([]);
    const [brokersMetadata, setBrokersMetadata] = useState<SimpleEntity[]>([]);
    const [paymentMethodsMetadata, setPaymentMethodsMetadata] = useState<SimpleEntity[]>([]);
    const [countriesMetadata, setCountriesMetadata] = useState<SimpleEntity[]>([]);
    const [selectedSubscription, setSelectedSubscription] = useState<ReportingSubscriptionRecord | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<ReportingSubscriptionRecord | null>(null);
    const [jobsSubscription, setJobsSubscription] = useState<ReportingSubscriptionRecord | null>(null);
    const [jobsResponse, setJobsResponse] = useState<ReportingSubscriptionJobsResponse>();
    const [jobsRequest, setJobsRequest] = useState<ReportingSubscriptionRequest>({
        length: 10,
        page: 1,
        sort_by: 'created_at',
        sort_direction: 1
    });

    const formModal = useComponentVisible(false);
    const deleteModal = useComponentVisible(false);
    const jobsModal = useComponentVisible(false);

    const [isDataTableLoading, setIsDataTableLoading] = useState(false);
    const [isForbidden, setIsForbidden] = useState(false);
    const [statusLoadingId, setStatusLoadingId] = useState<number | null>(null);
    const [deleteInProgress, setDeleteInProgress] = useState(false);
    const [jobsLoading, setJobsLoading] = useState(false);

    const filterType = dataTableRequest.reporting_type_id ?? 0;
    const filterStatus = dataTableRequest.status_id ?? 0;

    const requestWithFilter = useCallback((request: DataTableRequest): ReportingSubscriptionRequest => {
        const next: ReportingSubscriptionRequest = {...request};
        if (dataTableRequest.reporting_type_id) {
            next.reporting_type_id = dataTableRequest.reporting_type_id;
        } else {
            delete next.reporting_type_id;
        }
        if (dataTableRequest.status_id) {
            next.status_id = dataTableRequest.status_id;
        } else {
            delete next.status_id;
        }
        return next;
    }, [dataTableRequest.reporting_type_id, dataTableRequest.status_id]);

    const sendRequest = useCallback((request: ReportingSubscriptionRequest) => {
        setIsDataTableLoading(true);
        API.getReportingSubscriptions(request)
            .then((response: AxiosResponse) => {
                setSubscriptions(response.data as ReportingSubscriptionsResponse);
            })
            .catch(e => ErrorHandler(e, `get${APILink.GET_REPORTING_SUBSCRIPTIONS}`, toast, handleError, setIsForbidden))
            .finally(() => setIsDataTableLoading(false));
    }, [toast, handleError]);

    const fetchStaticData = useCallback(() => {
        API.getReportingTypes().then((response: AxiosResponse) => {
            const payload = response.data as { data?: ReportingType[] };
            setTypes(payload.data ?? []);
        }).catch(e => ErrorHandler(e, `get${APILink.GET_REPORTING_TYPES}`, toast, handleError));

        API.getUsersList(1).then((response: AxiosResponse) => {
            const payload = response.data as UserListResponse;
            setUsersList(payload.data ?? []);
        }).catch(e => ErrorHandler(e, `get${APILink.GET_USERS_LIST}`, toast, handleError));

        API.getBrokersList().then((response: AxiosResponse) => {
            const data = response.data.data as UserShortInfo[];
            setBrokersMetadata((data ?? []).map((broker) => ({
                id: typeof broker.id === 'string' ? parseInt(broker.id, 10) : Number(broker.id),
                name: broker.name ?? broker.email ?? `#${broker.id}`
            })));
        }).catch(e => ErrorHandler(e, `get${APILink.GET_BROKERS_LIST}`, toast, handleError));

        API.getPaymentMethodsList().then((response: AxiosResponse) => {
            const data = response.data.data as Array<{ id: number; name: string }>;
            setPaymentMethodsMetadata((data ?? []).map((method) => ({
                id: Number(method.id),
                name: method.name
            })));
        }).catch(e => ErrorHandler(e, `get${APILink.GET_PAYMENT_METHODS_LIST}`, toast, handleError));

        API.getCountriesShortList().then((response: AxiosResponse) => {
            const payload = response.data as CountriesShortResponse;
            setCountriesMetadata((payload.data ?? []).map((country) => ({
                id: country.id,
                name: country.name
            })));
        }).catch(e => ErrorHandler(e, `get${APILink.GET_COUNTRIES_SHORT_LIST}`, toast, handleError));
    }, [toast, handleError]);

    useEffect(() => {
        setTitle(t('admin.subscriptions.title'));
        dispatch(setSidebarTitle(t('admin.subscriptions.title')));
        if (user && user.user) {
            checkEnabled2FA(user.user).then(() => {
                fetchStaticData();
                sendRequest(dataTableRequest);
            }).catch(() => {
                // ignore
            });
        }
    }, [user, t, dispatch, fetchStaticData, sendRequest, dataTableRequest]);

    const onDataTableChanged = useCallback((request: DataTableRequest) => {
        const updatedRequest = requestWithFilter(request);
        persistDataTableRequest(updatedRequest);
        if (!updatedRequest.search || updatedRequest.search.length > 3) {
            sendRequest(!updatedRequest.search ? {...updatedRequest, search: ''} : updatedRequest);
        }
    }, [persistDataTableRequest, requestWithFilter, sendRequest]);

    const onFilterTypeChange = useCallback((value: string | number) => {
        let parsed = typeof value === 'string' ? parseInt(value, 10) : value;
        if (Number.isNaN(parsed) || parsed === 0) {
            parsed = 0;
        }
        const updatedRequest: ReportingSubscriptionRequest = {
            ...dataTableRequest,
            page: 1,
        };
        if (parsed > 0) {
            updatedRequest.reporting_type_id = parsed;
        } else {
            delete updatedRequest.reporting_type_id;
        }
        persistDataTableRequest(updatedRequest);
        sendRequest(updatedRequest);
    }, [dataTableRequest, persistDataTableRequest, sendRequest]);

    const onFilterStatusChange = useCallback((value: string | number) => {
        let parsed = typeof value === 'string' ? parseInt(value, 10) : value;
        if (Number.isNaN(parsed) || parsed === 0) {
            parsed = 0;
        }
        const updatedRequest: ReportingSubscriptionRequest = {
            ...dataTableRequest,
            page: 1,
        };
        if (parsed > 0) {
            updatedRequest.status_id = parsed;
        } else {
            delete updatedRequest.status_id;
        }
        persistDataTableRequest(updatedRequest);
        sendRequest(updatedRequest);
    }, [dataTableRequest, persistDataTableRequest, sendRequest]);

    const typeOptions = useMemo(() => types.map((type) => ({
        id: type.id,
        name: type.name
    })), [types]);

    const statusOptions = useMemo(() => {
        if (!system?.system?.statuses) {
            return [];
        }
        return system.system.statuses.map((status) => ({
            id: status.id,
            name: status.name
        }));
    }, [system]);

    const userFilterEntities = useMemo<SimpleEntity[]>(() => usersList.map((user) => ({
        id: parseInt(user.id, 10),
        name: user.name ?? user.email ?? `#${user.id}`
    })), [usersList]);

    const tableFilterLookups = useMemo<ReportingFilterLookups>(() => ({
        brokers: brokersMetadata,
        paymentMethods: paymentMethodsMetadata,
        countries: countriesMetadata,
        users: userFilterEntities,
        statuses: system?.system?.transactionStatuses?.map((status) => ({id: status.id, name: status.name})),
        providers: system?.system?.providers?.map((provider) => ({id: provider.id, name: provider.name})),
        currencies: system?.system?.currencies?.map((currency) => ({id: currency.id, code: currency.code}))
    }), [brokersMetadata, paymentMethodsMetadata, countriesMetadata, userFilterEntities, system]);

    const filterLabels: Record<FilterGroupKey, string> = useMemo(() => ({
        brokers: t('admin.subscriptions.filters.brokers'),
        users: t('admin.subscriptions.filters.users'),
        payment_methods: t('admin.subscriptions.filters.payment_methods'),
        providers: t('admin.subscriptions.filters.providers'),
        statuses: t('admin.subscriptions.filters.statuses'),
        countries: t('admin.subscriptions.filters.countries'),
        currencies: t('admin.subscriptions.filters.currencies')
    }), [t]);

    const mapFiltersToDisplay = useCallback((filters: ReportingSubscriptionRecord['filters']): FilterTagGroupDisplay[] => {
        const groups = buildReportingFilterGroups(filters, tableFilterLookups);
        return groups.map((group) => ({
            title: filterLabels[group.key],
            tags: group.tags
        }));
    }, [tableFilterLookups, filterLabels]);

    const modalMetadata = useMemo<ReportingFilterLookups>(() => ({
        brokers: brokersMetadata,
        paymentMethods: paymentMethodsMetadata,
        countries: countriesMetadata,
        users: userFilterEntities
    }), [brokersMetadata, paymentMethodsMetadata, countriesMetadata, userFilterEntities]);

    const formModalUI = useMemo(() => {
        if (!formModal.isComponentVisible) {
            return <></>;
        }
        return (
            <ReportingSubscriptionModal
                open={formModal.isComponentVisible}
                modalRef={formModal.ref}
                onClose={() => {
                    formModal.setIsComponentVisible(false);
                    setSelectedSubscription(null);
                }}
                onSuccess={() => sendRequest(dataTableRequest)}
                subscription={selectedSubscription}
                metadata={modalMetadata}
            />
        );
    }, [formModal, sendRequest, dataTableRequest, selectedSubscription, modalMetadata]);

    const openCreateModal = useCallback(() => {
        setSelectedSubscription(null);
        formModal.setIsComponentVisible(true);
        formModal.setIsComponentVisible(true);
    }, [formModal]);

    const openEditModal = useCallback((subscription: ReportingSubscriptionRecord) => {
        setSelectedSubscription(subscription);
        formModal.setIsComponentVisible(true);
    }, [formModal]);

    const onStatusToggle = useCallback((subscription: ReportingSubscriptionRecord, nextStatus: boolean) => {
        setStatusLoadingId(subscription.id);
        API.updateReportingSubscriptionStatus(subscription.id, nextStatus).then((response: AxiosResponse) => {
            const payload = response.data as { data?: ReportingSubscriptionRecord };
            if (payload.data && subscriptions) {
                setSubscriptions({
                    ...subscriptions,
                    data: subscriptions.data.map((item) => item.id === subscription.id ? payload.data as ReportingSubscriptionRecord : item)
                });
            }
            setStatusLoadingId(null);
            toast?.show(t('admin.subscriptions.status.updated'), 'bg-teal-500');
        }).catch(e => {
            setStatusLoadingId(null);
            ErrorHandler(e, `patch${APILink.UPDATE_REPORTING_SUBSCRIPTION_STATUS.replace('$subscription', subscription.id.toString())}`, toast, handleError);
        });
    }, [subscriptions, toast, t, handleError]);

    const openDeleteModal = useCallback((subscription: ReportingSubscriptionRecord) => {
        setDeleteTarget(subscription);
        deleteModal.setIsComponentVisible(true);
    }, [deleteModal]);

    const confirmDelete = useCallback(() => {
        if (!deleteTarget) {
            return;
        }
        setDeleteInProgress(true);
        API.deleteReportingSubscription(deleteTarget.id).then(() => {
            setDeleteInProgress(false);
            deleteModal.setIsComponentVisible(false);
            toast?.show(t('admin.subscriptions.deleted'), 'bg-teal-500');
            sendRequest(dataTableRequest);
        }).catch(e => {
            setDeleteInProgress(false);
            ErrorHandler(e, `delete${APILink.UPDATE_REPORTING_SUBSCRIPTION.replace('$subscription', deleteTarget.id.toString())}`, toast, handleError);
        });
    }, [deleteTarget, deleteModal, toast, t, sendRequest, dataTableRequest, handleError]);

    const openJobsModal = useCallback((subscription: ReportingSubscriptionRecord) => {
        setJobsSubscription(subscription);
        setJobsRequest({
            length: 10,
            page: 1,
            sort_by: 'created_at',
            sort_direction: 1
        });
        jobsModal.setIsComponentVisible(true);
        setJobsResponse(undefined);
        setJobsLoading(true);
        API.getReportingSubscriptionJobs(subscription.id, {
            length: 10,
            page: 1,
            sort_by: 'created_at',
            sort_direction: 1
        })
            .then((response: AxiosResponse) => {
                setJobsResponse(response.data as ReportingSubscriptionJobsResponse);
                setJobsLoading(false);
            })
            .catch(e => {
                setJobsLoading(false);
                ErrorHandler(e, `get${APILink.GET_REPORTING_SUBSCRIPTION_JOBS.replace('$subscription', subscription.id.toString())}`, toast, handleError);
            });
    }, [jobsModal, toast, handleError]);

    const changeJobsPage = useCallback((page: number) => {
        if (!jobsSubscription) {
            return;
        }
        const request = {...jobsRequest, page};
        setJobsLoading(true);
        API.getReportingSubscriptionJobs(jobsSubscription.id, request)
            .then((response: AxiosResponse) => {
                setJobsResponse(response.data as ReportingSubscriptionJobsResponse);
                setJobsRequest(request);
                setJobsLoading(false);
            })
            .catch(e => {
                setJobsLoading(false);
                ErrorHandler(e, `get${APILink.GET_REPORTING_SUBSCRIPTION_JOBS.replace('$subscription', jobsSubscription.id.toString())}`, toast, handleError);
            });
    }, [jobsRequest, jobsSubscription, toast, handleError]);

    const formatDate = useCallback((date?: string | null, emptyValue: string = '-') => {
        if (!date) {
            return emptyValue;
        }
        return moment(date).format('DD MMM YYYY HH:mm');
    }, []);

    const refresh = useCallback(() => {
        sendRequest({...dataTableRequest});
    }, [dataTableRequest]);

    const filterChips = useMemo<FilterChipDefinition[]>(() => {
        const chips: FilterChipDefinition[] = [];
        if (filterType > 0) {
            const typeName = typeOptions.find((option) => option.id === filterType)?.name ?? filterType.toString();
            chips.push({
                key: 'report-type',
                label: `${t('admin.subscriptions.filter.type')}: ${typeName}`,
                onRemove: () => onFilterTypeChange(0)
            });
        }
        if (filterStatus > 0) {
            const statusName = statusOptions.find((status) => status.id === filterStatus)?.name ?? filterStatus.toString();
            chips.push({
                key: 'status',
                label: `${t('admin.subscriptions.filter.status')}: ${statusName}`,
                onRemove: () => onFilterStatusChange(0)
            });
        }
        return chips;
    }, [filterType, typeOptions, t, onFilterTypeChange, filterStatus, statusOptions, onFilterStatusChange]);

    const actionsPanel = useMemo(() => {
        return (<div className="flex flex-row gap-2 lg:justify-end">
                <Button className="bg-transparent text-black dark:text-white shadow-none px-0.5"
                        onClick={() => refresh()}>
                    <FontAwesomeIcon
                        icon={solid('refresh')}
                        className="w-12 h-3"/>
                </Button>
                {user?.user.permissions.includes('create reporting subscription') ? <Button onClick={openCreateModal}
                                                                                  className="bg-black text-white">{t('admin.subscriptions.button.new')}</Button> : ''}
            </div>
        )
    }, [user, refresh]);

    const dataTable = useMemo(() => {
        if (!subscriptions) {
            return <></>;
        }
        return <DataTable {...dataTableRequest}
                          total_pages={subscriptions.pages.total}
                          count={subscriptions.count}
                          nodata={subscriptions.data.length === 0}
                          inProgress={isDataTableLoading}
                          rightPanel={actionsPanel}
                          filters={
                              <div className="w-full flex flex-col gap-0">
                                  <DropdownInput buttonClassName="min-h-[auto!important]" list={typeOptions}
                                                 searchable={true}
                                                 withLabel={true}
                                                 withAllButton={true}
                                                 key={`type`}
                                                 name={t('admin.subscriptions.filter.type')}
                                                 selected={filterType}
                                                 onChange={onFilterTypeChange}
                                  />
                                  {statusOptions.length > 0 && (
                                      <DropdownInput buttonClassName="min-h-[auto!important]"
                                                     list={statusOptions}
                                                     searchable={true}
                                                     withLabel={true}
                                                     withAllButton={true}
                                                     key={`status`}
                                                     name={t('admin.subscriptions.filter.status')}
                                                     selected={filterStatus}
                                                     onChange={onFilterStatusChange}
                                      />
                                  )}
                              </div>
                          }
                          fullWidthFilters={<FilterChips className="px-4 -mt-6 mb-6" items={filterChips}/>}
                          onChange={onDataTableChanged} headers={[
            {name: '#', sortBy: 'id'},
            {name: t('admin.subscriptions.table.report'), sortBy: 'reporting_type_id'},
            {name: t('admin.subscriptions.table.filters')},
            {name: t('admin.subscriptions.table.time'), sortBy: 'time'},
            {name: t('admin.subscriptions.table.schedule'), sortBy: 'schedule_id'},
            {name: t('admin.subscriptions.table.next_run'), sortBy: 'next_run_at'},
            {name: t('admin.subscriptions.table.status'), sortBy: 'status_id'},
            {name: t('admin.subscriptions.table.jobs'), sortBy: 'last_job_at'},
            {name: t('admin.subscriptions.table.actions'), className: 'text-right'}
        ]}>
            <tbody>
            {subscriptions.data.map((subscription, index) => (
                <tr key={subscription.id}>
                    <TD>{((dataTableRequest.page - 1) * dataTableRequest.length) + index + 1}</TD>
                    <TD>{subscription.report_name}</TD>
                    <TD>
                        <FilterTagGroups
                            groups={mapFiltersToDisplay(subscription.filters ?? null)}
                            emptyLabel={t('admin.subscriptions.filters.empty')}
                        />
                    </TD>
                    <TD>{subscription.time}</TD>
                    <TD>{subscription.schedule?.name ?? '-'}</TD>
                    <TD>{formatDate(subscription.next_run_at, '—')}</TD>
                    <TD>
                        <Switch
                            color="success"
                            checked={(subscription.status?.name ?? 'Active') === 'Active'}
                            onChange={(event) => onStatusToggle(subscription, event.target.checked)}
                            disabled={statusLoadingId === subscription.id}
                        />
                    </TD>
                    <TD>
                        <Button className="px-4 py-2 bg-black text-white"
                                onClick={() => openJobsModal(subscription)}>
                            {formatDate(subscription.last_job_at)}
                        </Button>
                    </TD>
                    <TD className="text-right">
                        <div className="flex items-center justify-end gap-2">
                            <Button className="px-3 py-2 text-white bg-black"
                                    onClick={() => openEditModal(subscription)}>
                                <FontAwesomeIcon icon={solid("pen-to-square")}/>
                            </Button>
                            <Button className="px-3 py-2 text-red-300 bg-black"
                                    onClick={() => openDeleteModal(subscription)}>
                                <FontAwesomeIcon icon={solid("trash")}/>
                            </Button>
                        </div>
                    </TD>
                </tr>
            ))}
            </tbody>
        </DataTable>;
    }, [subscriptions, dataTableRequest, isDataTableLoading, openCreateModal, t, typeOptions, filterType, onFilterTypeChange, onDataTableChanged, onStatusToggle, statusLoadingId, openJobsModal, openEditModal, openDeleteModal, formatDate]);

    const deleteModalUI = useMemo(() => {
        if (!deleteModal.isComponentVisible || !deleteTarget) {
            return <></>;
        }
        return <Modal refer={deleteModal.ref}
                      showModal={deleteModal.isComponentVisible}
                      title={t('admin.subscriptions.delete.title')}>
            <>
                <p className="text-center">{t('admin.subscriptions.delete.message', {name: deleteTarget.report_name})}</p>
                <Button className="mt-6 bg-red-500 text-white"
                        disabled={deleteInProgress}
                        onClick={confirmDelete}>{t('Delete')}</Button>
                <span className="mt-2 cursor-pointer text-gray-500"
                      onClick={() => deleteModal.setIsComponentVisible(false)}>{t('close')}</span>
            </>
        </Modal>;
    }, [deleteModal, deleteTarget, t, confirmDelete, deleteInProgress]);

    const jobsModalUI = useMemo(() => {
        if (!jobsModal.isComponentVisible || !jobsSubscription) {
            return <></>;
        }
        return <Modal refer={jobsModal.ref}
                      showModal={jobsModal.isComponentVisible}
                      title={t('admin.subscriptions.jobs.title')}>
            <div className="w-full">
                {jobsLoading && <div className="text-center py-4">Loading...</div>}
                {!jobsLoading && jobsResponse && jobsResponse.data.length === 0 &&
                    <div className="text-center text-gray-500">{t('admin.subscriptions.jobs.empty')}</div>}
                {!jobsLoading && jobsResponse && jobsResponse.data.length > 0 && (
                    <table className="w-full text-left">
                        <thead>
                        <tr>
                            <th className="py-2">{t('admin.subscriptions.jobs.recipient')}</th>
                            <th className="py-2">{t('admin.subscriptions.jobs.date')}</th>
                            <th className="py-2">{t('admin.subscriptions.jobs.status')}</th>
                        </tr>
                        </thead>
                        <tbody>
                        {jobsResponse.data.map((job: ReportingSubscriptionJob) => (
                            <tr key={job.id}>
                                <td className="py-2">{job.recipient.name ?? job.recipient.email}</td>
                                <td className="py-2">{formatDate(job.created_at)}</td>
                                <td className="py-2">{job.status?.label ?? job.status?.name ?? '-'}</td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                )}
                {jobsResponse && jobsResponse.pages.total > 1 && (
                    <div className="flex items-center justify-between mt-4">
                        <Button className="bg-black text-white"
                                disabled={jobsRequest.page === 1 || jobsLoading}
                                onClick={() => changeJobsPage(jobsRequest.page - 1)}>{t('admin.datatable.prev')}</Button>
                        <span>{jobsRequest.page} / {jobsResponse.pages.total}</span>
                        <Button className="bg-black text-white"
                                disabled={jobsRequest.page === jobsResponse.pages.total || jobsLoading}
                                onClick={() => changeJobsPage(jobsRequest.page + 1)}>{t('admin.datatable.next')}</Button>
                    </div>
                )}
            </div>
        </Modal>;
    }, [jobsModal, jobsSubscription, jobsResponse, jobsLoading, t, jobsRequest, changeJobsPage, formatDate]);

    return (
        <PageBlock>
            <Card className="page-card-block mt-6 bg-white dark:bg-blue-gray-900 dark:text-gray-500">
                <CardBody>
                    {dataTable}
                    {formModalUI}
                    {deleteModalUI}
                    {jobsModalUI}
                    <Forbidden isForbidden={isForbidden}/>
                </CardBody>
            </Card>
        </PageBlock>
    );
}

export default memo(Subscriptions);
